import React, { useReducer } from 'react';
import {
    Grid,
    Dialog,
    DialogTitle,
    Checkbox,
    Button,
    Typography,
    DialogContent,
    DialogActions,
    FormControlLabel,
    IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ViewColumnOutlinedIcon from '@material-ui/icons/ViewColumnOutlined';

import { makeStyles } from "@material-ui/core/styles";

const reduceTableColumns = (state, action) => {
    switch (action.type) {
        case 'change':
            return {
                ...state,
                [action.column]: !state[action.column]
            }
        default:
            return state;
    }
}

export const useTableColumnsToShow = () => {
    const [tableColumns, dispatchTableColumns] = useReducer(reduceTableColumns, {
        showID: false,
        showWarning: true,
        showName: true,
        showActive: true,
        showFace: true,
        showOnDevices: true,
        showSex: true,
        showDocType: true,
        showDocNumber: true,
        showPhone: true,
        showEmail: true,
        showPersonType: true,
        showLastEvent: false,
        showOrganization: true,
    });

    return { tableColumns, dispatchTableColumns };
}

const tableColumnOptions = [
    { column: 'showID', label: 'ID' },
    { column: 'showName', label: 'Nome' },
    { column: 'showActive', label: 'Ativo' },
    { column: 'showFace', label: 'Face' },
    { column: 'showOnDevices', label: 'Dispositivos' },
    { column: 'showSex', label: 'Sexo' },
    { column: 'showDocType', label: 'Tipo de documento' },
    { column: 'showDocNumber', label: 'N° do documento' },
    { column: 'showPhone', label: 'Telefone' },
    { column: 'showEmail', label: 'E-mail' },
    { column: 'showPersonType', label: 'Tipo de pessoa' },
    { column: 'showOrganization', label: 'Organização' },
    { column: 'showLastEvent', label: 'Último evento' },
];

export const ColumnsManagementModal = ({ open, setOpen, tableColumns, dispatchTableColumns }) => {
    const classes = useStyles();
    const visibleColumns = tableColumnOptions.filter(({ column }) => tableColumns[column]).length;

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="sm"
            classes={{ paper: classes.dialogWrapper }}
            BackdropProps={{ style: { backdropFilter: 'blur(3px)' } }}
        >
            <DialogTitle className={classes.dialogTitle}>
                <div className={classes.dialogTitleContent}>
                    <Typography variant="h6" component="div" className={classes.dialogTitleText}>
                        Campos da tabela
                    </Typography>
                    
                    <IconButton
                        aria-label="Fechar campos da tabela"
                        onClick={() => setOpen(false)}
                        className={classes.closeButton}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent dividers className={classes.dialogContent}>
                <div className={classes.columnsIntro}>
                    <div className={classes.columnsIntroIcon}>
                        <ViewColumnOutlinedIcon />
                    </div>
                    <div>
                        <Typography className={classes.columnsIntroTitle}>
                            Personalize a visualização
                        </Typography>
                        <Typography className={classes.columnsIntroText}>
                            Marque os campos que deseja exibir na lista de pessoas.
                        </Typography>
                    </div>
                    <span className={classes.visibleColumnsCount}>
                        {visibleColumns} visíveis
                    </span>
                </div>

                <div className={classes.columnsSection}>
                    <div className={classes.columnsSectionHeader}>
                        <div>
                            <Typography className={classes.columnsSectionTitle}>
                                Campos disponíveis
                            </Typography>
                            <Typography className={classes.columnsSectionDescription}>
                                As alterações são refletidas imediatamente na tabela.
                            </Typography>
                        </div>
                    </div>

                    <Grid container spacing={1}>
                        {tableColumnOptions.map(({ column, label }) => (
                            <Grid item xs={12} sm={6} key={column}>
                                <div className={`${classes.checkboxContainer} ${tableColumns[column] ? classes.checkboxContainerActive : ''}`}>
                                    <FormControlLabel
                                        label={
                                            <Typography className={classes.label}>
                                                {label}
                                            </Typography>
                                        }
                                        control={
                                            <Checkbox
                                                checked={tableColumns[column]}
                                                onChange={() => dispatchTableColumns({ type: 'change', column })}
                                                color="primary"
                                            />
                                        }
                                    />
                                   
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                </div>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpen(false)}
                    className={classes.doneButton}
                >
                    Concluir
                </Button>
            </DialogActions>
        </Dialog>
    )
};

const useStyles = makeStyles((theme) => ({
    dialogWrapper: {
        borderRadius: 12,
        maxHeight: 'calc(100vh - 64px)',
        width: '92%',
        [theme.breakpoints.down('xs')]: {
            margin: theme.spacing(2),
            maxHeight: 'calc(100vh - 32px)',
            width: 'calc(100% - 32px)',
        },
    },
    dialogTitle: {
        padding: theme.spacing(1.5, 2, 1.5, 3),
        [theme.breakpoints.down('xs')]: {
            paddingLeft: theme.spacing(2),
        },
    },
    dialogTitleContent: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
    },
    dialogTitleText: {
        color: '#172033',
        fontWeight: 600,
    },
    closeButton: {
        color: '#64748b',
        '&:hover': {
            backgroundColor: '#fef2f2',
            color: '#b91c1c',
        },
    },
    dialogContent: {
        padding: theme.spacing(2, 3),
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2),
        },
    },
    columnsIntro: {
        alignItems: 'center',
        background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
        border: '1px solid #dbeafe',
        borderRadius: 14,
        display: 'flex',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            padding: theme.spacing(1.5),
        },
    },
    columnsIntroIcon: {
        alignItems: 'center',
        backgroundColor: '#1976d2',
        borderRadius: 11,
        color: '#fff',
        display: 'flex',
        flexShrink: 0,
        height: 42,
        justifyContent: 'center',
        width: 42,
        '& svg': {
            fontSize: 22,
        },
    },
    columnsIntroTitle: {
        color: '#172033',
        fontSize: '1rem',
        fontWeight: 700,
    },
    columnsIntroText: {
        color: '#64748b',
        fontSize: '.82rem',
        lineHeight: 1.45,
        marginTop: 2,
    },
    visibleColumnsCount: {
        backgroundColor: '#fff',
        border: '1px solid #bfdbfe',
        borderRadius: 999,
        color: '#1565c0',
        fontSize: '.72rem',
        fontWeight: 700,
        marginLeft: 'auto',
        padding: '5px 10px',
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('xs')]: {
            marginLeft: 54,
        },
    },
    columnsSection: {
        backgroundColor: '#fbfdff',
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        padding: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            borderLeft: 0,
            borderRadius: 0,
            borderRight: 0,
            marginLeft: theme.spacing(-2),
            marginRight: theme.spacing(-2),
        },
    },
    columnsSectionHeader: {
        marginBottom: theme.spacing(1.5),
    },
    columnsSectionTitle: {
        color: '#1e293b',
        fontSize: '.95rem',
        fontWeight: 700,
    },
    columnsSectionDescription: {
        color: '#64748b',
        fontSize: '.78rem',
        lineHeight: 1.4,
        marginTop: 1,
    },
    label: {
        color: '#475569',
        fontSize: '.86rem',
        fontWeight: 600,
        userSelect: 'none',
    },
    checkboxContainer: {
        alignItems: 'center',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        display: 'flex',
        justifyContent: 'space-between',
        minHeight: 50,
        padding: theme.spacing(.25, 1),
        transition: 'border-color .2s ease, box-shadow .2s ease',
        '& .MuiFormControlLabel-root': {
            flex: 1,
            marginRight: 0,
        },
    },
    checkboxContainerActive: {
        borderColor: '#90caf9',
        boxShadow: '0 4px 12px rgba(25, 118, 210, .07)',
    },
    columnStatus: {
        backgroundColor: '#f1f5f9',
        borderRadius: 999,
        color: '#94a3b8',
        flexShrink: 0,
        fontSize: '.65rem',
        fontWeight: 700,
        padding: '3px 7px',
        textTransform: 'uppercase',
        '$checkboxContainerActive &': {
            backgroundColor: '#e3f2fd',
            color: '#1565c0',
        },
    },
    dialogActions: {
        borderTop: '1px solid #e5e7eb',
        padding: theme.spacing(2, 3),
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1.5, 2),
        },
    },
    doneButton: {
        borderRadius: 9,
        boxShadow: '0 6px 14px rgba(25, 118, 210, .2)',
        fontWeight: 700,
        minHeight: 42,
        minWidth: 160,
        textTransform: 'none',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
}));
